import { LoadingOutlined } from '@ant-design/icons';
import { Button, notification, Space } from 'antd';
import { ApiException, EventsClient, EventVm } from '../utils/api';
import { connectivityMonitor } from './connectivityMonitor';
import { flushEventMutation, serializeEventPayload } from './eventMutations';
import { newOutboxId, offlineEventStore, OutboxOp, OutboxRecord } from './offlineEventStore';
import {
    bumpFlushAttempt,
    FLUSH_HTTP_MS,
    FLUSH_STUCK_MS,
    MAX_AUTO_ATTEMPTS,
} from './outboxFlushPolicy';
import { eventsLookSame, restoreOptimisticFromOutbox } from './pendingEventHelpers';

const PLACEMENT = 'bottomRight';
const POLL_MS = 300;

export type OutboxListenerEvent =
    | { type: 'changed'; matchId?: number }
    | {
          type: 'published';
          matchId: number;
          clientEventKey: string;
          result: EventVm;
          op: OutboxOp;
      }
    | { type: 'cancelled'; matchId: number; clientEventKey: string; op: OutboxOp }
    /**
     * Connectivity restored and outbox drain finished (or Arena still down after
     * probe). Screens should refetch once - not in parallel with flush.
     */
    | { type: 'wentOnline' }
    /** User hit Sync now; flush outbox + refetch from server. */
    | { type: 'syncRequested' };

export interface EnqueueOutboxArgs {
    matchId: number;
    op: OutboxOp;
    label: string;
    clientEventKey: string;
    /** Create/Update command JSON, or { eventId } for delete. Sanitized on write. */
    payload: unknown;
    serverEventId?: number;
    /** Optimistic EventVm snapshot for UI + rehydrate. */
    optimisticEvent?: unknown;
    /** Publish flows through the 'published' subscription; only cancel is per-call. */
    onCancel?: () => void;
}

/**
 * IndexedDB-backed outbox: enqueue persists immediately, ~300ms poll flushes
 * FIFO when Arena is reachable. Row removed on HTTP 2xx before UI work.
 *
 * Reconnect order (important): probe Arena → drain outbox → emit wentOnline.
 * Parallel flush + full refetch is what caused stuck loaders and double rows.
 */
class EventOutboxQueue {
    private cancelHooks = new Map<string, () => void>();

    private listeners = new Set<(e: OutboxListenerEvent) => void>();

    private processing = false;

    private pollTimer: ReturnType<typeof setInterval> | null = null;

    private authPaused = false;

    /** After max auto attempts (or auth failure), skip auto-poll until Sync now / Retry. */
    private holdIds = new Set<string>();

    private bootstrapped = false;

    private wasOnline = true;

    /** Coalesce reconnect drains so we don't stack wentOnline storms. */
    private reconnectDrain: Promise<void> | null = null;

    /** True while drainAll runs - creates reconcile against server first. */
    private draining = false;

    /** Row ids currently inside flushOne (blocks tick/drain double-pick). */
    private inFlightIds = new Set<string>();

    public start = (): void => {
        if (this.bootstrapped) return;
        this.bootstrapped = true;
        connectivityMonitor.ensureStarted();
        this.wasOnline = connectivityMonitor.getIsOnline();
        connectivityMonitor.subscribe((online: boolean) => {
            if (online && !this.wasOnline) {
                void this.drainAndNotify('wentOnline');
            }
            this.wasOnline = online;
        });
        // Internet may already be up while Arena catches up a few seconds later.
        connectivityMonitor.subscribeFlushReady(() => {
            void this.drainAndNotify('wentOnline');
        });
        void offlineEventStore.resetFlushingToPending().then(() => {
            this.emit({ type: 'changed' });
            void this.tick();
        });
        if (!this.pollTimer) {
            this.pollTimer = setInterval(() => {
                void this.tick();
            }, POLL_MS);
        }
        void this.tick();
    };

    public subscribe = (listener: (e: OutboxListenerEvent) => void): (() => void) => {
        this.start();
        this.listeners.add(listener);
        return (): void => {
            this.listeners.delete(listener);
        };
    };

    public listByMatch = (matchId: number): Promise<OutboxRecord[]> =>
        offlineEventStore.listByMatch(matchId);

    public forceFlush = (): void => {
        void this.drainAndNotify('syncRequested');
    };

    public enqueue = async (args: EnqueueOutboxArgs): Promise<OutboxRecord | null> => {
        this.start();
        const payload = args.op === 'delete' ? args.payload : serializeEventPayload(args.payload);
        const record: OutboxRecord = {
            id: newOutboxId(),
            matchId: args.matchId,
            op: args.op,
            payload,
            clientEventKey: args.clientEventKey,
            serverEventId: args.serverEventId,
            label: args.label,
            createdAt: Date.now(),
            status: 'pending',
            optimisticEvent: args.optimisticEvent,
        };
        try {
            await offlineEventStore.add(record);
        } catch (error) {
            notification.error({
                message: 'Could not queue event locally',
                description: 'Storage full or blocked. Event was not saved.',
                placement: PLACEMENT,
                duration: 0,
            });
            // Screens paint the optimistic row before awaiting us; roll it back
            // instead of throwing into a void-ed promise.
            if (args.onCancel) args.onCancel();
            return null;
        }
        if (args.onCancel) this.cancelHooks.set(record.id, args.onCancel);
        this.showQueued(record);
        this.emit({ type: 'changed', matchId: record.matchId });
        void this.tick();
        return record;
    };

    public cancelRecord = async (id: string): Promise<void> => {
        const row = await offlineEventStore.get(id);
        if (!row) return;
        await offlineEventStore.remove(id);
        this.holdIds.delete(id);
        const onCancel = this.cancelHooks.get(id);
        this.cancelHooks.delete(id);
        notification.close(row.id);
        if (onCancel) onCancel();
        this.emit({
            type: 'cancelled',
            matchId: row.matchId,
            clientEventKey: row.clientEventKey,
            op: row.op,
        });
        this.emit({ type: 'changed', matchId: row.matchId });
    };

    public cancelByClientEventKey = async (clientEventKey: string): Promise<void> => {
        const row = await offlineEventStore.findByClientEventKey(clientEventKey);
        if (row) await this.cancelRecord(row.id);
    };

    public updatePendingCreate = async (
        clientEventKey: string,
        payload: unknown,
        optimisticEvent: unknown,
        label: string
    ): Promise<boolean> => {
        const row = await offlineEventStore.findByClientEventKey(clientEventKey);
        if (!row || row.op !== 'create' || row.status === 'flushing') return false;
        await offlineEventStore.update({
            ...row,
            payload: serializeEventPayload(payload),
            optimisticEvent,
            label,
            flushStartedAt: undefined,
        });
        this.holdIds.delete(row.id);
        this.emit({ type: 'changed', matchId: row.matchId });
        return true;
    };

    private emit = (e: OutboxListenerEvent): void => {
        this.listeners.forEach((l) => l(e));
    };

    /**
     * Probe Arena → drain outbox → notify screens to refetch. Coalesced so
     * reconnect + Sync now cannot stack parallel drains (that was the
     * double-POST source). 'wentOnline' only fires once Arena answered;
     * 'syncRequested' always answers the user's click.
     */
    private drainAndNotify = (type: 'wentOnline' | 'syncRequested'): Promise<void> => {
        if (!this.reconnectDrain) {
            this.reconnectDrain = (async () => {
                this.holdIds.clear();
                this.authPaused = false;
                this.draining = true; // block tick immediately
                try {
                    // Fresh Sync now / reconnect cycle gets another 3 auto tries.
                    await this.resetAttemptsForHeldOrPending();
                    const arenaOk = await connectivityMonitor.probeArenaNow();
                    if (arenaOk) await this.drainAll();
                    if (arenaOk || type === 'syncRequested') this.emit({ type });
                } finally {
                    this.draining = false;
                }
            })().finally(() => {
                this.reconnectDrain = null;
            });
        }
        return this.reconnectDrain;
    };

    /** Clear attempt counters so Sync now can re-run the auto-retry budget. */
    private resetAttemptsForHeldOrPending = async (): Promise<void> => {
        const rows = await offlineEventStore.listPending();
        await Promise.all(
            rows
                .filter((r) => (r.attempts ?? 0) > 0)
                .map((r) =>
                    offlineEventStore.update({
                        ...r,
                        attempts: 0,
                    })
                )
        );
    };

    /** Flush the oldest eligible row; false = nothing flushed (empty or held). */
    private flushNext = async (): Promise<boolean> => {
        await this.recoverStuckFlushing();
        const pending = (await offlineEventStore.listPending()).filter(
            (r) => r.status === 'pending' && !this.holdIds.has(r.id) && !this.inFlightIds.has(r.id)
        );
        if (pending.length === 0) return false;
        await this.flushOne(pending[0]);
        return !this.holdIds.has(pending[0].id);
    };

    /** Flush every pending row (FIFO), one at a time, until empty or blocked. */
    private drainAll = async (): Promise<void> => {
        // Soft cap so a poison row cannot spin forever inside drain.
        for (let i = 0; i < 50; i += 1) {
            if (this.authPaused || !connectivityMonitor.getCanFlush()) return;
            if (this.processing) {
                await this.waitWhileProcessing();
                continue;
            }
            this.processing = true;
            try {
                if (!(await this.flushNext())) return;
            } finally {
                this.processing = false;
            }
        }
    };

    private waitWhileProcessing = async (): Promise<void> => {
        for (let i = 0; i < 100 && this.processing; i += 1) {
            await new Promise((r) => setTimeout(r, 100));
        }
    };

    private tick = async (): Promise<void> => {
        // Never race the reconnect drain - that was double-POSTing the same row.
        if (this.processing || this.authPaused || this.draining || this.reconnectDrain) {
            return;
        }
        if (!connectivityMonitor.getCanFlush()) return;

        // Drain inside one processing window; releasing the flag between rows
        // let a poll tick pick the same FIFO head as the recursive call.
        this.processing = true;
        try {
            while (await this.flushNext()) {
                if (this.authPaused || !connectivityMonitor.getCanFlush()) break;
            }
        } finally {
            this.processing = false;
        }
    };

    /** Rows left in flushing (tab freeze / hung fetch) become pending again. */
    private recoverStuckFlushing = async (): Promise<void> => {
        const rows = await offlineEventStore.listPending();
        const now = Date.now();
        for (const row of rows) {
            if (row.status !== 'flushing') continue;
            const started = row.flushStartedAt ?? row.createdAt;
            if (now - started < FLUSH_STUCK_MS) continue;
            await this.failFlushAttempt(row, new Error('Arena did not respond in time'));
        }
    };

    /**
     * Shared path for HTTP timeout / error / stuck flush: bump attempts, auto-retry
     * until MAX_AUTO_ATTEMPTS, then hold for Sync now.
     */
    private failFlushAttempt = async (row: OutboxRecord, error: unknown): Promise<void> => {
        const current = (await offlineEventStore.get(row.id)) ?? row;
        const { attempts, holdForManual } = bumpFlushAttempt(current.attempts);
        const updated: OutboxRecord = {
            ...current,
            status: 'pending',
            flushStartedAt: undefined,
            attempts,
            needsReconcile: current.op === 'create' ? true : current.needsReconcile,
        };
        await offlineEventStore.update(updated);
        notification.close(row.id);

        if (this.isAuthError(error)) {
            this.holdIds.add(row.id);
            this.authPaused = true;
            this.showAuthError(updated, error);
        } else if (holdForManual) {
            this.holdIds.add(row.id);
            this.showError(updated, error);
        } else {
            // Still in auto-retry budget - keep polling, show which try we are on.
            this.holdIds.delete(row.id);
            this.showRetrying(updated);
        }
        this.emit({ type: 'changed', matchId: row.matchId });
    };

    private flushOne = async (row: OutboxRecord): Promise<void> => {
        if (this.inFlightIds.has(row.id)) return;
        this.inFlightIds.add(row.id);
        try {
            const runFlush = async (): Promise<void> => {
                // Under the lock: if another worker already finished this row, stop.
                // Without this check, a second flusher re-put the deleted row and
                // POSTed the same create again (audit showed 2-5 identical 201s).
                const fresh = await offlineEventStore.get(row.id);
                if (!fresh) return;

                const flushing: OutboxRecord = {
                    ...fresh,
                    status: 'flushing',
                    flushStartedAt: Date.now(),
                };
                await offlineEventStore.update(flushing);
                this.showLoading(fresh);
                try {
                    // Always fingerprint-match creates before POST. Covers lost-ack
                    // retries and duplicate outbox rows for the same tap.
                    const reconciled =
                        fresh.op === 'create' ? await this.reconcileExistingCreate(fresh) : null;
                    const result =
                        reconciled ??
                        (await this.withTimeout(
                            flushEventMutation(fresh.op, fresh.payload, fresh.serverEventId),
                            FLUSH_HTTP_MS,
                            'Arena did not respond in time'
                        ));

                    // Re-check before emit: if we were racing, row may already be gone.
                    const stillThere = await offlineEventStore.get(fresh.id);
                    if (!stillThere) return;

                    await offlineEventStore.remove(fresh.id);
                    this.holdIds.delete(fresh.id);
                    this.cancelHooks.delete(fresh.id);
                    connectivityMonitor.forceOnline();
                    notification.close(fresh.id);
                    this.showSuccess(fresh);
                    this.emit({
                        type: 'published',
                        matchId: fresh.matchId,
                        clientEventKey: fresh.clientEventKey,
                        result,
                        op: fresh.op,
                    });
                    this.emit({ type: 'changed', matchId: fresh.matchId });
                } catch (error) {
                    await this.failFlushAttempt(fresh, error);
                }
            };

            const locks = (
                navigator as unknown as {
                    locks?: { request: (name: string, cb: () => Promise<void>) => Promise<void> };
                }
            ).locks;
            if (locks?.request) {
                await locks.request('twa-event-outbox', runFlush);
            } else {
                await runFlush();
            }
        } finally {
            this.inFlightIds.delete(row.id);
        }
    };

    /**
     * Before create POST: if server already has a fingerprint twin, treat as
     * published (avoids double submit after hung/lost response or double flush).
     */
    private reconcileExistingCreate = async (row: OutboxRecord): Promise<EventVm | null> => {
        if (row.op !== 'create') return null;
        // Prefer payload fingerprint (always present) over optimistic snapshot.
        const probe = this.fingerprintEventFromRow(row);
        if (!probe) return null;
        try {
            const serverEvents = await this.withTimeout(
                new EventsClient().getByMatchId(row.matchId),
                FLUSH_HTTP_MS,
                'Arena did not respond in time'
            );
            const match = serverEvents.find((e) => e.id > 0 && eventsLookSame(e, probe));
            return match ?? null;
        } catch {
            // Fall through to normal POST; timeout/error handled by caller.
            return null;
        }
    };

    /** Build a minimal EventVm from outbox payload/optimistic for fingerprinting. */
    private fingerprintEventFromRow = (row: OutboxRecord): EventVm | null => {
        if (row.optimisticEvent) {
            return restoreOptimisticFromOutbox(row.optimisticEvent, row.clientEventKey);
        }
        const payload = row.payload as Record<string, unknown> | null;
        if (!payload || payload.typeId == null) return null;
        try {
            return restoreOptimisticFromOutbox(payload, row.clientEventKey);
        } catch {
            return null;
        }
    };

    private withTimeout = <T,>(p: Promise<T>, ms: number, message: string): Promise<T> =>
        new Promise<T>((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error(message)), ms);
            p.then(
                (v) => {
                    clearTimeout(timer);
                    resolve(v);
                },
                (err) => {
                    clearTimeout(timer);
                    reject(err);
                }
            );
        });

    private isAuthError = (error: unknown): boolean => {
        if (error instanceof ApiException) return error.status === 401 || error.status === 403;
        return false;
    };

    private showQueued = (row: OutboxRecord): void => {
        // Spinner only when a flush can actually start (internet AND Arena);
        // otherwise it would spin forever while Arena is down.
        if (connectivityMonitor.getCanFlush()) {
            this.showLoading(row);
            return;
        }
        notification.open({
            key: row.id,
            message: 'Queued offline',
            description: row.label,
            placement: PLACEMENT,
            duration: 3,
        });
    };

    private showLoading = (row: OutboxRecord): void => {
        notification.open({
            key: row.id,
            message: 'Saving event',
            description: row.label,
            icon: <LoadingOutlined spin style={{ color: '#1890ff' }} />,
            placement: PLACEMENT,
            duration: 0,
        });
    };

    private showRetrying = (row: OutboxRecord): void => {
        const n = row.attempts ?? 1;
        notification.open({
            key: row.id,
            message: `Retrying save (${n}/${MAX_AUTO_ATTEMPTS})`,
            description: row.label,
            icon: <LoadingOutlined spin style={{ color: '#1890ff' }} />,
            placement: PLACEMENT,
            duration: 0,
        });
    };

    private showSuccess = (row: OutboxRecord): void => {
        notification.success({
            key: row.id,
            message: 'Event saved',
            description: row.label,
            placement: PLACEMENT,
            duration: 3,
        });
    };

    private showAuthError = (row: OutboxRecord, error: unknown): void => {
        notification.error({
            key: row.id,
            message: 'Login required to sync',
            description: this.describeError(row, error),
            placement: PLACEMENT,
            duration: 0,
            btn: (
                <Button
                    type="primary"
                    size="small"
                    onClick={(): void => {
                        this.authPaused = false;
                        this.holdIds.delete(row.id);
                        notification.close(row.id);
                        void this.tick();
                    }}
                >
                    Retry
                </Button>
            ),
        });
    };

    private showError = (row: OutboxRecord, error: unknown): void => {
        const retry = (): void => {
            // Same path as Sync now for this row: clear hold + reset attempt budget.
            notification.close(row.id);
            this.holdIds.delete(row.id);
            void offlineEventStore.get(row.id).then((current) => {
                if (current) {
                    void offlineEventStore.update({ ...current, attempts: 0 });
                }
                void this.tick();
            });
        };
        const cancel = (): void => {
            void this.cancelRecord(row.id);
        };
        notification.error({
            key: row.id,
            message: 'Event failed to save',
            description: `${this.describeError(row, error)} Use Sync now (or Retry) to try again.`,
            placement: PLACEMENT,
            duration: 0,
            btn: (
                <Space>
                    <Button size="small" onClick={cancel}>
                        Cancel
                    </Button>
                    <Button type="primary" danger size="small" onClick={retry}>
                        Retry
                    </Button>
                </Space>
            ),
        });
    };

    private describeError = (row: OutboxRecord, error: unknown): string => {
        let detail = 'Unknown error. Please retry.';
        if (error instanceof ApiException) {
            detail = this.formatApiError(error);
        } else if (error && typeof error === 'object' && 'message' in error) {
            detail = String((error as { message?: unknown }).message ?? detail);
        } else if (typeof error === 'string') {
            detail = error;
        }
        return `${row.label}: ${detail}`;
    };

    private formatApiError = (error: ApiException): string => {
        const raw = typeof error.response === 'string' ? error.response : '';
        if (!raw) return error.message || `HTTP ${error.status}`;
        try {
            const parsed = JSON.parse(raw) as {
                title?: string;
                errors?: Record<string, string[]>;
            };
            if (parsed.errors) {
                const parts = Object.entries(parsed.errors).flatMap(([k, v]) =>
                    (v || []).map((msg) => `${k}: ${msg}`)
                );
                if (parts.length) return parts.slice(0, 3).join('; ');
            }
            if (parsed.title) return parsed.title;
        } catch {
            // not JSON
        }
        return raw.length > 180 ? `${raw.slice(0, 180)}…` : raw;
    };
}

export const eventSubmissionQueue = new EventOutboxQueue();
